/**
 * Brand-derived CSS custom properties for the Automations surface.
 *
 * The whole surface is tenant-themed through --gmba-primary, but a brand hex
 * alone is not enough to build a palette from:
 *
 *  - Tints. Every soft fill (selected rows, icon tiles, the "add a step"
 *    dash, the live-preview box) is the brand at 5–15% alpha. Hardcoding
 *    those as rgba(27,89,142,…) meant a tenant whose brand is pink got pink
 *    borders sitting on blue fills. --gmba-primary-rgb exposes the channels
 *    so the stylesheet can build any alpha off the real brand color.
 *
 *  - Text. A mid-tone brand color used as label text usually fails contrast
 *    on white. --gmba-primary-ink is the brand darkened just enough to clear
 *    4.5:1, so brand-colored text stays readable whatever the tenant picked.
 *
 * Kept in plain JS (no color-mix()/relative-color CSS) so the result is
 * identical in every browser the app supports.
 */

const clamp255 = (n) => Math.max(0, Math.min(255, Math.round(n)));

/** Parse #rgb / #rrggbb (with or without the hash) → {r,g,b} | null. */
export const parseHex = (hex) => {
  if (typeof hex !== "string") return null;
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

/** WCAG relative luminance for an {r,g,b} in 0–255. */
const luminance = ({ r, g, b }) => {
  const ch = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
};

/** Contrast ratio of an {r,g,b} against white. */
const contrastOnWhite = (rgb) => 1.05 / (luminance(rgb) + 0.05);

const toHex = ({ r, g, b }) =>
  `#${[r, g, b].map((v) => clamp255(v).toString(16).padStart(2, "0")).join("")}`;

/**
 * Darken `rgb` in small steps until it clears `target` contrast on white.
 * Steps are multiplicative so hue and saturation hold while value drops.
 */
export const inkFor = (rgb, target = 4.5) => {
  let cur = { ...rgb };
  for (let i = 0; i < 40 && contrastOnWhite(cur) < target; i += 1) {
    cur = { r: cur.r * 0.93, g: cur.g * 0.93, b: cur.b * 0.93 };
  }
  return { r: clamp255(cur.r), g: clamp255(cur.g), b: clamp255(cur.b) };
};

/**
 * The custom properties to spread onto a `.gmba` root's inline style.
 * Falls back to the house blue if the tenant color is missing or malformed.
 */
export const brandTokens = (primary) => {
  const rgb = parseHex(primary) || parseHex("#1B598E");
  const ink = inkFor(rgb);
  return {
    "--gmba-primary": primary || "#1B598E",
    "--gmba-primary-rgb": `${rgb.r}, ${rgb.g}, ${rgb.b}`,
    "--gmba-primary-ink": toHex(ink),
  };
};

export default brandTokens;
